var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useFetch, usePromise } from '@rad/hooks';
import { DEFAULT_PARCEL_KIND } from '../../../../client/types';
import { getParcels, createParcel } from '../../../common/network/parcels';
import AddressSearchField from './address-search-field';
export const emptyAddressForm = { parcelIds: [''] };
const AddressField = ({ label, selected, parcels, onSelect }) => {
    var _a;
    const [query, setQuery] = useState((_a = selected === null || selected === void 0 ? void 0 : selected.address) !== null && _a !== void 0 ? _a : '');
    const [confirmed, setConfirmed] = useState(!!selected);
    const [resolving, setResolving] = useState(false);
    const select = (parcel) => {
        setQuery(parcel.address);
        setConfirmed(true);
        onSelect(parcel);
    };
    const handleQueryChange = (value) => {
        setQuery(value);
        setConfirmed(false);
    };
    const handleCandidateChosen = (candidate) => __awaiter(void 0, void 0, void 0, function* () {
        const dbMatch = parcels.find(p => p.address.toLowerCase() === candidate.address.toLowerCase());
        if (dbMatch) {
            select(dbMatch);
            return;
        }
        setResolving(true);
        try {
            const created = yield createParcel({
                point: candidate.location,
                address: candidate.address,
                kind: DEFAULT_PARCEL_KIND,
            })();
            select(created.parcel);
        }
        finally {
            setResolving(false);
        }
    });
    return (_jsx(AddressSearchField, { parcels: parcels, query: query, onQueryChange: handleQueryChange, confirmed: confirmed, busy: resolving, onParcelChosen: select, onCandidateChosen: handleCandidateChosen, label: label, placeholder: "Enter street address, city, state", classNames: { label: 'visually-hidden' } }));
};
export const PropertyAddressForm = ({ parcelIds, onChange }) => {
    var _a;
    const [parcelOptionsPromise, refetchParcels] = useFetch(getParcels);
    const parcels = (_a = usePromise(parcelOptionsPromise)) !== null && _a !== void 0 ? _a : [];
    // Each field holds what was typed into it, so rows are keyed by identity rather than position:
    // removing one takes its field with it instead of leaving that text in the row below.
    const [rowKeys, setRowKeys] = useState(() => parcelIds.map((_, i) => i));
    const addAddressRow = () => {
        setRowKeys([...rowKeys, Math.max(-1, ...rowKeys) + 1]);
        onChange({ parcelIds: [...parcelIds, ''] });
    };
    const removeAddressRow = (index) => {
        setRowKeys(rowKeys.filter((_, i) => i !== index));
        onChange({ parcelIds: parcelIds.filter((_, i) => i !== index) });
    };
    const selectParcel = (index, parcel) => {
        const updated = [...parcelIds];
        updated[index] = parcel.id;
        onChange({ parcelIds: updated });
        refetchParcels();
    };
    // The fields share one visible label, so each gets its own hidden one and the remove buttons line up
    // with the inputs. A single address has nothing to remove.
    return (_jsxs("fieldset", { children: [_jsx("legend", { className: "form-label fs-6", children: parcelIds.length > 1 ? 'Addresses' : 'Address' }), parcelIds.map((val, i) => (_jsxs("div", { className: "d-flex gap-2 align-items-start mb-2", children: [_jsx("div", { className: "flex-grow-1", children: _jsx(AddressField, { label: `Address ${i + 1}`, selected: parcels.find(p => p.id === val), parcels: parcels, onSelect: (parcel) => selectParcel(i, parcel) }) }), parcelIds.length > 1 && (_jsx("button", { type: "button", onClick: () => removeAddressRow(i), className: "btn btn-outline-secondary", "aria-label": `Remove address ${i + 1}`, title: "Remove this address", children: _jsx("i", { className: "bi bi-trash", "aria-hidden": "true" }) }))] }, rowKeys[i]))), _jsxs("button", { type: "button", onClick: addAddressRow, className: "btn btn-outline-secondary", children: [_jsx("i", { className: "bi bi-plus-lg me-1", "aria-hidden": "true" }), "Add another address"] })] }));
};
